import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { billingService } from '../billingService';
import { CreatePaymentDto, CreateInvoiceDto, InvoiceItemDiscount } from '../../../types/billing';

/**
 * Invoice and payment changes can create, move or clear an IPD referral payout:
 * the flat commission accrues the moment the admission bill is fully settled
 * (and is withdrawn if a refund reopens it). Refresh the payout ledger alongside
 * the billing caches so Finance → Payouts never shows a stale empty list.
 */
function invalidatePayoutQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['payee-outstanding'] });
  queryClient.invalidateQueries({ queryKey: ['payee-accruals'] });
  queryClient.invalidateQueries({ queryKey: ['payout-vouchers'] });
  queryClient.invalidateQueries({ queryKey: ['revenue-distributions'] });
  queryClient.invalidateQueries({ queryKey: ['revenue-distributions-summary'] });
  queryClient.invalidateQueries({ queryKey: ['doctor-payments'] });
}

export function useTopSheet(dateFrom?: string, dateTo?: string) {
  return useQuery({
    queryKey: ['top-sheet', dateFrom, dateTo],
    queryFn: () => billingService.getTopSheet(dateFrom, dateTo),
    staleTime: 15000,
    // The Top Sheet consolidates several ledgers, so always pull a fresh copy
    // when it is opened rather than relying on cached cross-module state.
    refetchOnMount: 'always',
  });
}

/** Filterable due-collection list; fetched lazily when the card modal opens. */
export function useDueCollections(
  filters: {
    dateFrom?: string;
    dateTo?: string;
    bucket?: 'current' | 'old';
    setting?: 'opd' | 'ipd' | 'pharmacy';
    search?: string;
    page?: number;
    limit?: number;
  },
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['due-collections', filters],
    queryFn: () => billingService.getDueCollections(filters),
    placeholderData: (previous) => previous,
    staleTime: 15000,
    enabled: options.enabled ?? true,
  });
}

export function useInvoices(
  page = 1,
  limit = 20,
  filters: { search?: string; status?: string; patientId?: string; dateFrom?: string; dateTo?: string } = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['invoices', page, limit, filters],
    queryFn: () => billingService.getInvoices(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
    enabled: options.enabled ?? true,
  });
}

/** Invoice totals (billed / paid / due) for a single patient profile. */
export function usePatientInvoiceSummary(
  patientId?: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['patient-invoice-summary', patientId],
    queryFn: () => billingService.getPatientInvoiceSummary(patientId!),
    enabled: Boolean(patientId) && (options.enabled ?? true),
    staleTime: 30000,
  });
}

export function useTrashedInvoices(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['invoices-trash', page, limit, search],
    queryFn: () => billingService.getTrashedInvoices(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 15000,
  });
}

export function useInvoiceTrashCount() {
  return useQuery({
    queryKey: ['invoices-trash-count'],
    queryFn: () => billingService.getTrashCount(),
    staleTime: 15000,
  });
}

export function useRestoreInvoices() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => billingService.restoreInvoices(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices-trash'] });
      queryClient.invalidateQueries({ queryKey: ['invoices-trash-count'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
    },
  });
}

export function usePermanentlyDeleteInvoices() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => billingService.permanentlyDeleteInvoices(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices-trash'] });
      queryClient.invalidateQueries({ queryKey: ['invoices-trash-count'] });
    },
  });
}

export function usePayments(filters?: { search?: string; invoiceId?: string; dateFrom?: string; dateTo?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['payments', filters],
    queryFn: () => billingService.getPayments(filters),
    staleTime: 30000,
  });
}

export function useRecordPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Payment recorded successfully' },
    mutationFn: billingService.recordPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
      queryClient.invalidateQueries({ queryKey: ['admission-billing-status'] });
      queryClient.invalidateQueries({ queryKey: ['admission-charges'] });
      queryClient.invalidateQueries({ queryKey: ['admissions'] });
      invalidatePayoutQueries(queryClient);
    },
  });
}

export function useProcessRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: billingService.processRefund,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
      // A refund reopens the bill, which withdraws any pending commission.
      invalidatePayoutQueries(queryClient);
    },
  });
}

/**
 * Bulk-delete payments as a single silent mutation. The backend rebalances the
 * affected invoices and cashbook entries, so we refresh all of those caches.
 */
export function useBulkDeletePayments() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => billingService.bulkDeletePayments(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['payments-trash'] });
      queryClient.invalidateQueries({ queryKey: ['payments-trash-count'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook-summary'] });
      // Removing payments can reopen a settled bill and withdraw its payout.
      invalidatePayoutQueries(queryClient);
    },
  });
}

/** Trashed payments: amount already excluded from every collection total. */
export function useTrashedPayments(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['payments-trash', page, limit, search],
    queryFn: () => billingService.getTrashedPayments(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 15000,
  });
}

export function usePaymentTrashCount() {
  return useQuery({
    queryKey: ['payments-trash-count'],
    queryFn: () => billingService.getPaymentTrashCount(),
    staleTime: 15000,
  });
}

export function useRestorePayments() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => billingService.restorePayments(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments-trash'] });
      queryClient.invalidateQueries({ queryKey: ['payments-trash-count'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook-summary'] });
      // Restoring a collection can settle the bill again and re-accrue payouts.
      invalidatePayoutQueries(queryClient);
    },
  });
}

export function usePermanentlyDeletePayments() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => billingService.permanentlyDeletePayments(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments-trash'] });
      queryClient.invalidateQueries({ queryKey: ['payments-trash-count'] });
    },
  });
}

export function useCashbook(filters?: { dateFrom?: string; dateTo?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['cashbook', filters],
    queryFn: () => billingService.getCashbook(filters),
    staleTime: 30000,
  });
}

/**
 * Bulk-delete cashbook entries as a single silent mutation. The backend
 * recomputes each affected day's running balance, so we refresh the cashbook,
 * its summary and the dependent dashboard/top-sheet caches.
 */
export function useBulkDeleteCashbook() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => billingService.bulkDeleteCashbook(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook-summary'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook-trash'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook-trash-count'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
    },
  });
}

export function useTrashedCashbook(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['cashbook-trash', page, limit, search],
    queryFn: () => billingService.getTrashedCashbook(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 15000,
  });
}

export function useCashbookTrashCount() {
  return useQuery({
    queryKey: ['cashbook-trash-count'],
    queryFn: () => billingService.getCashbookTrashCount(),
    staleTime: 15000,
  });
}

export function useRestoreCashbook() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => billingService.restoreCashbook(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashbook-trash'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook-trash-count'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook-summary'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
    },
  });
}

export function usePermanentlyDeleteCashbook() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => billingService.permanentlyDeleteCashbook(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashbook-trash'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook-trash-count'] });
    },
  });
}

export function useCashbookSummary(filters?: { dateFrom?: string; dateTo?: string }) {
  return useQuery({
    queryKey: ['cashbook-summary', filters],
    queryFn: () => billingService.getCashbookSummary(filters),
    staleTime: 30000,
  });
}

export function useUnbilledCharges(visitId?: string) {
  return useQuery({
    queryKey: ['unbilled-charges', visitId],
    queryFn: () => billingService.getUnbilledCharges(visitId!),
    enabled: !!visitId,
    staleTime: 10000,
  });
}

export function useInvoiceItems(invoiceId?: string) {
  return useQuery({
    queryKey: ['invoice-items', invoiceId],
    queryFn: () => billingService.getInvoiceItems(invoiceId!),
    enabled: !!invoiceId,
    staleTime: 10000,
  });
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: billingService.createInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['unbilled-charges'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['admission-billing-status'] });
      queryClient.invalidateQueries({ queryKey: ['admission-charges'] });
      queryClient.invalidateQueries({ queryKey: ['admissions'] });
      // The final invoice can settle an IPD bill and accrue its commission.
      invalidatePayoutQueries(queryClient);
    },
  });
}

export function useInvoice(id?: string) {
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: () => billingService.getInvoice(id!),
    enabled: !!id,
    staleTime: 15000,
  });
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateInvoiceDto> & { notes?: string; items?: InvoiceItemDiscount[] } }) =>
      billingService.updateInvoice(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      invalidatePayoutQueries(queryClient);
    },
  });
}

/**
 * Change an invoice's status by hand (cancel / reopen). Silent so the caller
 * can surface a context-specific toast (e.g. "Invoice cancelled").
 */
export function useUpdateInvoiceStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      billingService.updateInvoiceStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      invalidatePayoutQueries(queryClient);
    },
  });
}

/**
 * Deleting an invoice releases its charges back to unbilled and changes the
 * visit's Total / Paid / Due, so every admission surface is invalidated too —
 * not just the invoice screens.
 */
export function useDeleteInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { successMessage: 'Invoice deleted successfully' },
    mutationFn: (id: string) => billingService.deleteInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['invoices-trash-count'] });
      queryClient.invalidateQueries({ queryKey: ['invoices-trash'] });
      queryClient.invalidateQueries({ queryKey: ['admissions'] });
      queryClient.invalidateQueries({ queryKey: ['admission-charges'] });
      queryClient.invalidateQueries({ queryKey: ['admission-billing-status'] });
      queryClient.invalidateQueries({ queryKey: ['unbilled-charges'] });
      invalidatePayoutQueries(queryClient);
    },
  });
}

/**
 * Bulk-delete invoices via the transactional backend endpoint. Invoices that
 * still have payments are reported back as `blocked` instead of being removed.
 */
export function useBulkDeleteInvoices() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => billingService.bulkDeleteInvoices(ids),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['invoices-trash-count'] });
      queryClient.invalidateQueries({ queryKey: ['invoices-trash'] });
      // Released charges are unbilled again, so admission totals move too.
      queryClient.invalidateQueries({ queryKey: ['admissions'] });
      queryClient.invalidateQueries({ queryKey: ['admission-charges'] });
      queryClient.invalidateQueries({ queryKey: ['admission-billing-status'] });
      queryClient.invalidateQueries({ queryKey: ['unbilled-charges'] });
      invalidatePayoutQueries(queryClient);
    },
  });
}

/**
 * Record payments against several invoices as one silent mutation so the UI can
 * show a single summary toast instead of one per invoice.
 */
export function useBulkRecordPayments() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (payments: CreatePaymentDto[]) => {
      for (const payment of payments) {
        await billingService.recordPayment(payment);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
      // A full settlement books the admission's referral payout immediately.
      invalidatePayoutQueries(queryClient);
    },
  });
}

export function useBillingOpenCashbook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ openingBalance, enteredById, shiftCode }: { openingBalance: number; enteredById: string; shiftCode?: string }) =>
      billingService.openCashbook(openingBalance, enteredById, shiftCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook-summary'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
    },
  });
}

export function useBillingCloseCashbook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ closingBalance, enteredById, shiftCode, varianceReason }: {
      closingBalance: number;
      enteredById: string;
      shiftCode?: string;
      varianceReason?: string;
    }) => billingService.closeCashbook(closingBalance, enteredById, shiftCode, varianceReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook-summary'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
    },
  });
}
